import React, { useState, useCallback } from 'react';
import { Factory as FactoryIcon, Package, Warehouse, Users, FileText, FolderOpen, BarChart3, Settings, LogOut, ShoppingCart, Landmark } from 'lucide-react';
import Calculator from './Calculator';
import ProductsPage from './ProductsPage';
import StockPage from './StockPage';
import CustomersPage from './CustomersPage';
import InvoicesPage from './InvoicesPage';
import DocumentsPage from './DocumentsPage';
import AnalyticsPage from './AnalyticsPage';
import PurchasePage from './PurchasePage';
import FinancePage from './FinancePage';
import SettingsPage from './SettingsPage';
import { Button } from './ui/button';

const MainLayout = ({ onLogout, environment }) => {
  const [activePage, setActivePage] = useState('factory');
  const [editingProduct, setEditingProduct] = useState(null);
  const env = environment || 'test';
  const envStyles = env === 'production'
    ? { label: 'PROD', wrap: 'bg-rose-500/15 border-rose-500/40 text-rose-300', dot: 'bg-rose-400' }
    : { label: 'TEST', wrap: 'bg-amber-500/15 border-amber-500/40 text-amber-300', dot: 'bg-amber-400' };

  const navItems = [
    { id: 'factory', label: 'Factory', icon: FactoryIcon },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'stock', label: 'Stock', icon: Warehouse },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'documents', label: 'Documents', icon: FolderOpen },
    { id: 'purchase', label: 'Purchase', icon: ShoppingCart },
    { id: 'finance', label: 'Finance', icon: Landmark },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  ];

  // Handle editing a product from Products page
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setActivePage('factory');
  };

  // Clear editing state when done
  const handleProductSaved = useCallback(() => {
    setEditingProduct(null);
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {/* Sidebar - hidden in print */}
      <div className="w-16 sm:w-48 bg-zinc-900 border-r border-zinc-800 flex flex-col no-print">
        {/* Logo area */}
        <div className="p-3 sm:p-4 border-b border-zinc-800">
          <img 
            src="/suteko-logo.png" 
            alt="Suteko Steel" 
            className="h-8 sm:h-10 object-contain mx-auto sm:mx-0"
          />
        </div>

        {/* Environment badge - sticky reminder of which DB is active */}
        <div className="px-2 sm:px-3 py-2 border-b border-zinc-800">
          <div
            className={`flex items-center justify-center sm:justify-start gap-1.5 px-2 py-1 rounded-md border text-[10px] sm:text-xs font-bold tracking-wide ${envStyles.wrap}`}
            data-testid="env-badge"
            title={env === 'production' ? 'Production data — real records' : 'Test data — safe to experiment'}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${envStyles.dot}`} />
            <span>{envStyles.label}</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 sm:p-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activePage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActivePage(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-zinc-800 text-white' 
                    : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="hidden sm:block text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Bottom section */}
        <div className="p-2 sm:p-3 border-t border-zinc-800 space-y-1">
          <button
            onClick={() => setActivePage('settings')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              activePage === 'settings'
                ? 'bg-zinc-800 text-white'
                : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200'
            }`}
          >
            <Settings className="w-5 h-5 flex-shrink-0" />
            <span className="hidden sm:block text-sm font-medium">Settings</span>
          </button>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-500 hover:bg-zinc-800/50 hover:text-red-400 transition-colors"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span className="hidden sm:block text-sm font-medium">Logout</span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        {activePage === 'factory' && (
          <Calculator 
            editingProduct={editingProduct}
            onProductSaved={handleProductSaved}
          />
        )}
        {activePage === 'products' && (
          <ProductsPage onEditProduct={handleEditProduct} />
        )}
        {activePage === 'stock' && (
          <StockPage />
        )}
        {activePage === 'customers' && (
          <CustomersPage />
        )}
        {activePage === 'invoices' && (
          <InvoicesPage />
        )}
        {activePage === 'documents' && (
          <DocumentsPage />
        )}
        {activePage === 'purchase' && (
          <PurchasePage />
        )}
        {activePage === 'finance' && (
          <FinancePage />
        )}
        {activePage === 'analytics' && (
          <AnalyticsPage />
        )}
        {activePage === 'settings' && (
          <SettingsPage />
        )}
      </div>
    </div>
  );
};

export default MainLayout;
